import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import TextButton from '@oberoninternal/travelbase-ds/components/action/TextButton';
import { Box, Flex } from '@rebass/grid';
import { gql } from '@apollo/client';
import isBefore from 'date-fns/isBefore';
import { FormattedMessage, useIntl } from 'react-intl';
import { OrderItem_Ticket_Fragment, OrderItemFragment, TicketStatus } from '../generated/graphql';
import createImgProps from '../utils/createImgProps';
import createTicketText from '../utils/createTicketText';
import downloadTorFile from '../utils/downloadTORFile';
import OrderItem, {
    OrderItemButton,
    OrderItemDisclaimer,
    OrderItemInfo,
    OrderItemName,
    OrderItemProps,
    OrderItemTextButton,
    OrderItemThumbnail,
} from './designsystem/OrderItem';
import Download from './svg/Download';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import AsyncButton from './AsyncButton';
import RatingStar from './svg/RatingStar';
import Stack from './Stack';
import { useDeviceSize } from '@oberoninternal/travelbase-ds/context/devicesize';
import Link from 'next/link';
import ReviewModal from './ReviewModal';

interface OrderTicketProps extends OrderItemProps {
    data: OrderItem_Ticket_Fragment;
    onLeaveActivityReviewClick?: () => void;
}

export const fragment = gql`
    fragment OrderTicket on Ticket {
        id
        downloadVoucherUrl
        ticketStatus: status
        ...ReviewModal
    }
`;

export const orderItemIsTicket = (item: OrderItemFragment): item is OrderItem_Ticket_Fragment =>
    item.__typename === 'Ticket';

const getDisclaimerText = (status: TicketStatus, voucher?: string | null) => {
    switch (status) {
        case 'ACCEPTED': {
            if (!voucher) {
                return <FormattedMessage defaultMessage="Je kunt je ticket downloaden zodra het restant is betaald" />;
            }
            return <FormattedMessage defaultMessage="Je tickets zijn nu beschikbaar" />;
        }
        case 'WAIVED':
            return <FormattedMessage defaultMessage="Ticket vervallen" />;
        default:
            return null;
    }
};

const OrderTicket = ({ data, onLeaveActivityReviewClick, linkProps, ...props }: OrderTicketProps) => {
    const intl = useIntl();
    const readReviewModal = useSesame();
    const device = useDeviceSize();
    const { activity, activityReview, timeslot, rateLines, ticketStatus, downloadVoucherUrl } = data;

    if (!activity || !timeslot) {
        return null;
    }
    const { name, place } = activity;
    const title = `${name} - ${place}`;
    const disclaimerText = getDisclaimerText(ticketStatus, downloadVoucherUrl);
    const ticketIsDone = isBefore(new Date(timeslot.endDateTime), new Date());
    return (
        <OrderItem {...props}>
            <Flex alignItems={['left', 'center']} flex={1} flexDirection={['column', null, null, 'row']}>
                {activity?.jpegThumbnail && activity.webpThumbnail && (
                    <OrderItemThumbnail {...createImgProps(activity.jpegThumbnail, activity.webpThumbnail)} />
                )}

                <Box my={[0, 4, null, null, null, 0]} ml={[0, null, 4, null, null, 0]} mx={2}>
                    <OrderItemInfo>
                        {disclaimerText && <OrderItemDisclaimer>{disclaimerText}</OrderItemDisclaimer>}
                        <OrderItemName>
                            {linkProps?.href ? (
                                <Link passHref {...linkProps} target="_blank" rel="noreferrer" legacyBehavior>
                                    {title}
                                </Link>
                            ) : (
                                title
                            )}
                        </OrderItemName>
                        <Body variant="small">
                            <span style={{ textTransform: 'capitalize' }}>
                                <FormattedMessage defaultMessage="activiteit" />
                            </span>
                            {', '}
                            {createTicketText(intl, timeslot.startDateTime, rateLines)}
                        </Body>
                    </OrderItemInfo>
                </Box>
                <Stack
                    mt={4}
                    flex={device === 'mobile' || device === 'tablet' ? 1 : 'unset'}
                    variant={device === 'mobile' || device === 'tablet' ? 'default' : 'inline'}
                    spacing={4}
                    style={{ zIndex: 1 }}
                >
                    {!activityReview && ticketIsDone && onLeaveActivityReviewClick && (
                        <AsyncButton onClick={onLeaveActivityReviewClick}>
                            <RatingStar />
                            <span>
                                <FormattedMessage defaultMessage="Beoordelen" />
                            </span>
                        </AsyncButton>
                    )}
                    {ticketIsDone && activityReview && (
                        <>
                            <OrderItemTextButton onClick={readReviewModal.onOpen} as={TextButton} size="tiny">
                                <FormattedMessage defaultMessage="Beoordeling lezen" />
                            </OrderItemTextButton>
                            <ReviewModal data={data} open={readReviewModal.open} onClose={readReviewModal.onClose} />
                        </>
                    )}
                </Stack>
            </Flex>
            {!ticketIsDone && downloadVoucherUrl && (
                <OrderItemButton onClick={() => downloadTorFile(downloadVoucherUrl, name)}>
                    <Download />
                    <span>
                        <FormattedMessage defaultMessage="Tickets" />
                    </span>
                </OrderItemButton>
            )}
        </OrderItem>
    );
};

export default OrderTicket;
